import * as logger from '../utils/logger';
import * as messageInterceptor from '../utils/message-interceptor';
import { replaceUserPlaceholder } from '../utils/medias';
import { getRestartUrl } from './gvp';
import { getKeyValues } from './config';
import { getCurrentTracks } from './audio-subtitles';
import { startSetup } from './youbora';
import { getTitle } from './bi';
import { messages, streamType } from '../constants';
import { commercializationTypes } from '../constants/types';
import { validateSchema } from './schema-validator';

const playerManager = cast.framework.CastReceiverContext.getInstance().getPlayerManager();
const log = logger.create('Seek');
let isSeekLiveSuccess = false,
  contentUrl = null,
  restartUrl = null,
  isVspp = false,
  isStartOver = false,
  startTime = 0,
  cdnBuffer = -1,
  originalRequest = {},
  playlistDelay = 0;

/**
 * Seek to time on live content
 * @param {Integer} currentTime Timestamp to be seeked
 */
export async function onSeekLive(currentTime) {
  log.info('Seek to:', currentTime);
  const date = new Date(currentTime);
  log.info('Seek to (HH:MM:SS)', date.toLocaleTimeString());
  const correctionCurrentTime = currentTime < startTime ? startTime : currentTime;
  if (isInsideCDNBuffer(correctionCurrentTime)) {
    // Currently on VSPP
    if (isVspp) {
      setIsVspp(false);
      reloadVideo(contentUrl, correctionCurrentTime);
    } else {
      playerManager.seek(
        playerManager.getMediaTimeForAbsoluteTime(toSeconds(correctionCurrentTime))
      );
      isSeekLiveSuccess = true;
    }
  } else {
    // Currently on VSPP
    if (isVspp) {
      playerManager.seek(
        playerManager.getMediaTimeForAbsoluteTime(toSeconds(correctionCurrentTime))
      );
      isSeekLiveSuccess = true;
    } else if (isStartOver) {
      // Seek is outside CDN buffer and channel has StartOver (VSPP)
      setIsVspp(true);
      if (restartUrl) {
        reloadVideo(restartUrl, correctionCurrentTime);
      } else {
        try {
          let restartUrl = await getRestartUrl(originalRequest.customData, startTime);
          restartUrl = replaceUserPlaceholder(restartUrl);
          setRestartUrl(restartUrl);
          reloadVideo(restartUrl, correctionCurrentTime);
        } catch (e) {
          log.error('Restart URL failed', e);
          setIsVspp(false);
          onSeekLive(Date.now() - toMilliSeconds(getBuffer() - 10));
        }
      }
    } else {
      // Seek is outside CDN buffer an channel doesn't have StartOver (VSPP)
      // We should seek to the limit of the CDN buffer.
      // 5 seconds are added to ensure we are inside CDN Buffer.
      onSeekLive(Date.now() - toMilliSeconds(getBuffer() - 10));
    }
  }
}

/**
 * Convert to ms
 * @param {Integer} seconds
 * @return {Integer} milliseconds
 */
export function toMilliSeconds(seconds) {
  return seconds * 1000;
}

/**
 * Convert to s
 * @param {Integer} milliseconds
 * @return {Integer} seconds
 */
export function toSeconds(milliseconds) {
  return milliseconds / 1000;
}

/**
 * Checks if startTime of the program is inside buffer.
 * Absence of seekTime means we are loading live edge.
 * @param {number} seekTime Time to be reproduced.
 * @returns {Boolean} True if it is inside the buffer.
 */
export function isInsideCDNBuffer(seekTime) {
  if (seekTime) {
    const now = toSeconds(Date.now());
    return now - getBuffer() < toSeconds(seekTime);
  } else {
    return true;
  }
}

/**
 * Seek to present on live content
 */
export function onLaunchLive() {
  onSeekLive(Date.now());
}

/**
 * Seek to beginnig on live content
 */
export function onLaunchStartOver() {
  onSeekLive(startTime);
}

/**
 * Get CDN buffer from LCH or key value.
 * @returns {number} CDN buffer in seconds.
 */
function getBuffer() {
  return cdnBuffer === -1 ? getKeyValues().cdnBuffer : cdnBuffer;
}

/**
 * Load new url (restart) using current media
 * @param {string} url Restart url
 * @param {number} time Current time on playback
 */
function reloadVideo(url, time) {
  log.info('Reload using new url:', url);
  startSetup({
    title: originalRequest.customData.liveChannel.title,
    program: getTitle(),
    duration: 0,
    resource: url,
    type: streamType.live,
    isLive: true,
    metadata: {
      contentId: originalRequest.customData.liveChannel.id
    },
    quality: originalRequest.customData.medias[0].quality,
    commercializationType: isVspp ? commercializationTypes.vspp : commercializationTypes.cdn,
    itemType: originalRequest.customData.itemType
  });
  delete originalRequest.media.customData;
  // currentTime: 0 even when seeking forward. Otherwise there's a chance
  // a new program is playing and info gets updated.
  playerManager.load({
    ...originalRequest,
    currentTime: 0,
    media: {
      ...originalRequest.media,
      contentUrl: url,
      streamType: cast.framework.messages.StreamType.LIVE
    },
    customData: {
      ...originalRequest.customData,
      liveStartTime: time,
      audio: getCurrentTracks().audio
    }
  });
}

/**
 * Send current time of a live stream using a timestamp in milliseconds.
 */
export function sendProgress() {
  if (isSeekLiveSuccess) {
    messageInterceptor.broadcast(messages.seekSuccess);
    isSeekLiveSuccess = false;
  } else {
    const streamProgress = {
      currentTime: toMilliSeconds(
        Math.round(playerManager.getAbsoluteTimeForMediaTime(playerManager.getCurrentTimeSec()))
      )
    };
    validateSchema('streamProgress', streamProgress);
    log.info('Progress broadcasted', streamProgress);
    const date = new Date(streamProgress.currentTime);
    log.info('Progress broadcasted (HH:MM:SS)', date.toLocaleTimeString());
    messageInterceptor.broadcast(messages.streamProgress, streamProgress);
  }
}

/**
 * Sets current url being played.
 * @param {string} url Current content's url.
 */
export function setContentUrl(url) {
  contentUrl = url;
}

/**
 * Sets restart url.
 * @param {string} url VSPP url.
 */
export function setRestartUrl(url) {
  restartUrl = url;
}

/**
 * Sets VSPP playback.
 * @param {boolean} vspp Currently VSPP or not.
 */
export function setIsVspp(vspp) {
  isVspp = vspp;
}

/**
 * Gets VSPP playback.
 * @returns {boolean} Currently VSPP or not
 */
export function getIsVspp() {
  return isVspp;
}

/**
 * Sets current content start time.
 * @param {number} time Start time in s.
 */
export function setStartTime(time) {
  startTime = time;
}

/**
 * Sets current channel SO availability.
 * @param {boolean} startOver Current SO or not.
 */
export function setIsStartOver(startOver) {
  isStartOver = startOver;
}

/**
 * Sets current channel's buffer.
 * @param {number} buffer Buffer in s.
 */
export function setCdnBuffer(buffer) {
  cdnBuffer = buffer;
}

/**
 * Sets original load request.
 * @param {Object} request Load request.
 */
export function setOriginalRequest(request) {
  originalRequest = request;
}

/**
 * Sets playlist experience delay from live edge (s).
 * A delay of 0s means fist playback of a content.
 * @param {boolean} delay Seconds away from live (0 means beginning).
 */
export function setPlaylistDelay(delay) {
  playlistDelay = delay;
}

/**
 * Gets playlist experience delay.
 * @returns {boolean} Delay on current content (s).
 */
export function getPlaylistDelay() {
  return playlistDelay;
}
